import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Grid,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TabContext, TabPanel } from '@mui/lab';
import { AlertAdvice } from 'components/common/alert-advice';
import { CustomTab as Tab } from 'components/common/custom-tab';
import { ContainedButton } from 'components/ContainedButton';
import { PropertiesMap } from 'components/PropertiesMap';
import clsx from 'clsx';
import React, { FC } from 'react';
import {
  AMOUNTS_TAB,
  LEGAL_DESCRIPTION_TAB,
  OWENERSHIP_TAB,
  PROPERTY_TAB,
  SUBDIVISIONS_TAB,
  SURVEY_TAB
} from 'utils/constants';

import { DeleteConfirmationModal } from '../../../components/delete-confirmation';
import SkeletonProperties from './components/skeleton-properties';
import { TabSurvey } from './components/survey/survey';
import { TabAmounts } from './components/tab-amounts/tab-amount';
import { TabLegalDescription } from './components/tab-legal-description/tab-legal-description';
import { TabProperty } from './components/tab-property/tab-property';
import { TabSubdivision } from './components/tab-subdivision/tab-subdivision';
import { useOrderFlowProperties } from './use-order-flow-properties';
import { getDesktopMapWidth } from './utils/getDesktopMapWidth';
import { TabOwnership } from './components/tab-ownership/tab-ownership';

// DESIGN.md §9: tablets render md/lg layouts with a coarse pointer.
const COARSE_POINTER = '@media (hover: none) and (pointer: coarse)';

const useStyles = makeStyles((theme: any) => ({
  headerContainer: {
    borderBottomColor: theme.accent.main,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(2)
  },
  headerTitle: {
    fontWeight: 500,
    color: theme.palette.primary.main
  },
  plusbutton: {
    marginLeft: theme.spacing(2),
    [COARSE_POINTER]: { minWidth: 44, minHeight: 44 },
  },
  actionsCoarse: {
    [COARSE_POINTER]: { '& .MuiButton-root': { minHeight: 44 } },
  },
  textPlus: { color: theme.palette.primary.contrastText, fontWeight: 500 },
  panelcontainer: { padding: theme.spacing(1), },
  plusbuttonMobile: { minWidth: theme.spacing(6), minHeight: theme.spacing(6) },
  headerMobile: { flexWrap: 'nowrap', alignItems: 'center' },
  actionsMobile: { width: '100%', margin: 0 },
  actionItemMobile: { flex: 1, minWidth: 0 },
  tabsMobile: { maxWidth: '100%', minHeight: theme.spacing(6) },
  contentMobile: { minWidth: 0, width: '100%' },
  fullWidth: {
    [theme.breakpoints.down('md')]: { width: '100%' },
  },
}));

const MOBILE_ACTION_STYLE = { minHeight: 48, width: '100%' };

type Props = {
  transactionType: string
}

export const Properties: FC<Props> = ({ transactionType }) => {
  const {
    t,
    theme,
    properties,
    isSaveLoading,
    onRemoveProperty,
    isRemovePropertyLoading,
    selectedProperty,
    selectProperty,
    currentTab,
    onSelectTab,
    onSaveProperty,
    onAddProperty,
    errors,
    dataIsValid,
    control,
    getValues,
    isCreatePropertyError,
    isUpdatePropertyError,
    setValue,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    isPropertiesDataLoading,
    onConfirmRemovePropertyInformation,
    deleteErrorMessage,
    setDeleteErrorMessage,
    deletePropertyInformationLoading,
    errorMessage
  } = useOrderFlowProperties();
  const classes = useStyles(theme);
  // < 960px (phones + tablets): map stacks above the selector/content panel.
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <PropertiesMap
        properties={properties}
        onSelectProperty={selectProperty}
        selectedPropertyId={selectedProperty._id}
        direction={isCompact ? 'vertical' : 'horizontal'}
        mapWidth={isCompact ? undefined : getDesktopMapWidth(theme)}
        squareMap
        ctaMapControls
        header={(
          <Grid
            container
            justifyContent="space-between"
            className={clsx(classes.headerContainer, isCompact && classes.headerMobile)}
          >
            <Typography
              variant="h6"
              className={classes.headerTitle}
            >
              {t('orders:properties')}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              className={clsx(classes.plusbutton, isCompact && classes.plusbuttonMobile)}
              onClick={() => {
                onAddProperty();
                selectProperty('');
              }}
              disabled={selectedProperty._id === ''}
            >
              <Typography
                variant="caption"
                className={classes.textPlus}
              >
                <FontAwesomeIcon size="lg" icon="plus" color={theme.palette.primary.main} />
              </Typography>
            </Button>
          </Grid>
        )}
      >
        {isPropertiesDataLoading ? <SkeletonProperties />
          : (
            <TabContext value={currentTab}>
              <Grid container direction="column" className={clsx(isCompact && classes.contentMobile)}>
                <Grid
                  item
                  container
                  justifyContent="flex-end"
                  spacing={2}
                  className={clsx(classes.actionsCoarse, isCompact && classes.actionsMobile)}
                  style={{ padding: theme.spacing(0.5) }}
                >
                  <Grid item className={clsx(isCompact && classes.actionItemMobile)}>
                    <ContainedButton
                      text={t('common:remove')}
                      icon="trash"
                      customStyle={isCompact ? MOBILE_ACTION_STYLE : undefined}
                      onClick={() => setIsDeleteModalOpen(true)}
                      disabled={!selectedProperty._id}
                    />
                  </Grid>
                  <Grid item className={clsx(isCompact && classes.actionItemMobile)}>
                    <ContainedButton
                      text={t('common:save')}
                      icon="hdd"
                      customStyle={isCompact ? MOBILE_ACTION_STYLE : undefined}
                      onClick={onSaveProperty}
                      isLoading={isSaveLoading}
                      disabled={!dataIsValid}
                    />
                  </Grid>
                </Grid>
                {Object.keys(errors).length > 0 && (
                  <Grid item>
                    <AlertAdvice show message={t('orders:complete-all-fields')} />
                  </Grid>
                )}
                {(isCreatePropertyError || isUpdatePropertyError) && (
                  <Grid item>
                    <AlertAdvice show message={errorMessage} />
                  </Grid>
                )}
                <Grid item style={isCompact ? { minWidth: 0, maxWidth: '100%' } : undefined}>
                  <Tabs
                    className={clsx(isCompact && classes.tabsMobile)}
                    variant={isCompact ? 'scrollable' : 'standard'}
                    scrollButtons={isCompact ? 'auto' : false}
                    value={currentTab}
                    onChange={(e, value: string) => onSelectTab(value)}
                  >
                    <Tab
                      value={PROPERTY_TAB}
                      currentValue={currentTab}
                      text={t('orders:property')}
                    />
                    <Tab
                      value={LEGAL_DESCRIPTION_TAB}
                      currentValue={currentTab}
                      text={t('orders:legal-description')}
                    />
                    <Tab
                      value={SUBDIVISIONS_TAB}
                      currentValue={currentTab}
                      text={t('orders:subdivisions')}
                    />
                    <Tab
                      value={SURVEY_TAB}
                      currentValue={currentTab}
                      text={t('orders:survey')}
                    />
                    <Tab
                      value={AMOUNTS_TAB}
                      currentValue={currentTab}
                      text="Amounts"
                      tooltipTitle={selectedProperty.accounting.length <= 0 ? t('properties:property-must-be-created-first') : ""}
                      disabled={selectedProperty.accounting.length <= 0}
                    />
                    <Tab
                      value={OWENERSHIP_TAB}
                      currentValue={currentTab}
                      text={t('orders:ownership')}
                    />
                  </Tabs>
                </Grid>
                <Grid item className={classes.fullWidth}>
                  <TabPanel value={PROPERTY_TAB} className={classes.panelcontainer}>
                    <TabProperty
                      control={control}
                      getValues={getValues}
                      setValue={setValue}
                    />
                  </TabPanel>
                  <TabPanel value={LEGAL_DESCRIPTION_TAB} className={classes.panelcontainer}>
                    <TabLegalDescription
                      control={control}
                    />
                  </TabPanel>
                  <TabPanel value={SUBDIVISIONS_TAB} className={classes.panelcontainer}>
                    <TabSubdivision
                      control={control}
                    />
                  </TabPanel>
                  <TabPanel value={SURVEY_TAB} className={classes.panelcontainer}>
                    <TabSurvey
                      control={control}
                    />
                  </TabPanel>
                  <TabPanel value={AMOUNTS_TAB} className={classes.panelcontainer}>
                    <TabAmounts
                      control={control}
                      propertyId={selectedProperty._id}
                      selectedProperty={selectedProperty}
                      transactionType={transactionType}
                    />
                  </TabPanel>
                  <TabPanel value={OWENERSHIP_TAB} className={classes.panelcontainer}>
                    <TabOwnership
                      selectedProperty={selectedProperty}
                    />
                  </TabPanel>
                </Grid>
              </Grid>
            </TabContext>
          )}
      </PropertiesMap>
      <DeleteConfirmationModal
        title={t('properties:remove-property')}
        confirmationMessage={t('properties:remove-confirmation')}
        isOpen={isDeleteModalOpen}
        isRemoveLoading={isRemovePropertyLoading}
        onConfirm={onRemoveProperty}
        onClose={() => setIsDeleteModalOpen(false)}
      />
      <DeleteConfirmationModal
        title={t('properties:remove-property')}
        confirmationMessage={t('properties:hard-remove-confirmation')}
        isOpen={deleteErrorMessage !== ""}
        isRemoveLoading={deletePropertyInformationLoading}
        onConfirm={onConfirmRemovePropertyInformation}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteErrorMessage("");
        }}
      />
    </>
  );
};
